/**
 * pages/risk/reference/checklistStore.js — Prework checklist лавлах (DB-backed).
 *
 * Эх сурвалжийн ЭРЭМБЭ (fallback гинжин хэлхээ) — хуучин 15 үргэлж харагдана:
 *   1) DB (xab.prework_checklist)         — migration ажилласан бол ЭНЭ ноогдол
 *   2) Хуучин v1 localStorage             — DB хоосон/бэлэн биш бол ТАНЫ хуучин өгөгдөл
 *   3) Built-in анхдагч seed (доор)       — хоёул хоосон бол сүүлчийн fallback
 *
 * Ингэснээр DB migration ажиллаагүй ч /risk/reference/checklist дээр 15 асуумж
 * "хуучин байснаараа" харагдана. DB бэлэн болбол засвар DB-д хадгалагдана.
 *
 * Хоёр төрөл: "5A" (6 асуумж) ба "CRITICAL" (15 критик хяналт — Чухал хяналт.pptx).
 */
import { xabPreworkChecklistApi } from "src/api/xabApi";

const KEY = "risk_checklist_ref_v2"; // офлайн кэш
const LEGACY_KEY = "risk_checklist_ref_v1"; // хуучин localStorage эх сурвалж

// ── Built-in анхдагч seed (хамгийн сүүлчийн fallback) ──────────────────────
// process flow HSD-2026 -> "5А АМИН ДАДАЛ ЭЗЭМШИХ ПРОЦЕСС"-ын Орц багана.
// (Дэлгэрэнгүй тайлбар нь DB-д — ERP баганаас авсан.)
const DEFAULT_5A = [
  "1А алжаалаа тайлах",
  "2А аюулыг илрүүлэх",
  "3А ажлаа бодох",
  "4А тасралтгүй суралцах",
  "5А зөв гүйцэтгэх",
];
const DEFAULT_CRITICAL = [
  "Өндрөөс унах",
  "Цахилгаанд цохиулах",
  "Хөдөлгөөнт техник ба явган зорчигчийн мөргөлдөөн",
  "Түгжээгүй энерги (LOTO алдагдах)",
  "Ачаа өргөх үйл ажиллагаа (Кран, оосорлогоо)",
  "Хязгаарлагдмал орчин (Хийн хордлого)",
  "Нуралт ба чулуулаг унах (Ил уурхай)",
  "Тэсэлгээний ажлын аюулгүй байдал",
  "Химийн бодисын түлэгдэлт/хордлого",
  "Гал түймэр ба тэсрэлт",
  "Конвейерийн системд хавчуулагдах",
  "Даралтат сав, шугам хоолойн задрал",
  "Усанд живэх (Хаягдлын аж ахуй, шүүрлийн сан)",
  "Механик тоноглолын эд анги шидэгдэх",
  "Техникийн мөргөлдөөн (Хүнд даацын автомашин)",
];

const makeDefaults = (type) => {
  // Зөвхөн мэдэгдэж буй 2 төрөлд анхдагч байна. 5A_NARYAD (хуучин наряд
  // даалгаврын хуудас) нь DB-ээс л ирнэ — эс бөгөөс шинэ 5А-гийн агуулгыг
  // хуучин таб дээр буруу харуулах байсан.
  if (type !== "CRITICAL" && type !== "5A") return [];
  const seed = type === "CRITICAL" ? DEFAULT_CRITICAL : DEFAULT_5A;
  return seed.map((title, i) => ({
    id: `def-${type}-${i + 1}`,
    no: i + 1,
    title,
    description: "",
    requires_photo: false,
    is_critical: type === "CRITICAL",
    created_at: null,
    updated_at: null,
  }));
};

// Санах ойн кэш — синхрон listChecklist-д хэрэглэнэ.
let _cache = { "5A": [], CRITICAL: [] };

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && typeof d === "object" ? d : null;
  } catch {
    return null;
  }
};

const saveLocal = (data) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* noop */
  }
};

/** Хуучин v1 localStorage-оос тухайн төрлийн жагсаалтыг унших. */
const loadLegacyV1 = (type) => {
  const d = readJson(LEGACY_KEY);
  const list = d && Array.isArray(d[type]) ? d[type] : [];
  return list;
};

/** DB хоосон/бэлэн биш үеийн fallback: хуучин v1 → built-in анхдагч. */
const fallbackFor = (type) => {
  const legacy = loadLegacyV1(type);
  if (legacy.length) return legacy;
  return makeDefaults(type);
};

// Анхны ачаалалд кэшийг v2 → v1 → анхдагчаас дүүргэх (эхний рендер хоосон биш).
(() => {
  const d = readJson(KEY);
  ["5A", "CRITICAL"].forEach((t) => {
    const cached = d && Array.isArray(d[t]) ? d[t] : [];
    _cache[t] = cached.length ? cached : fallbackFor(t);
  });
})();

const sortByNo = (list) =>
  [...(list || [])].sort((a, b) => (a.no || 0) - (b.no || 0));

const setCache = (type, list) => {
  _cache[type] = sortByNo(list);
  saveLocal(_cache);
};

/** DB-ээс татаж кэш шинэчлэх (үндсэн — async). DB хоосон бол fallback. */
export const loadChecklist = async (type) => {
  try {
    const rows = await xabPreworkChecklistApi.list(type);
    const list = Array.isArray(rows) ? rows : [];
    if (list.length) {
      setCache(type, list);
      return sortByNo(list);
    }
    // DB хоосон (migration ажиллаагүй / seed алга) → хуучин 15
    const fb = fallbackFor(type);
    setCache(type, fb);
    return sortByNo(fb);
  } catch (e) {
    // API алдаа/офлайн → хуучин 15
    const fb = fallbackFor(type);
    setCache(type, fb);
    return sortByNo(fb);
  }
};

/** Кэшээс шууд (синхрон) — эхний рендерийн fallback. */
export const listChecklist = (type) => {
  const c = _cache[type] || [];
  return sortByNo(c.length ? c : fallbackFor(type));
};

/** Нэгийг авах (кэшээс) */
export const getChecklistItem = (type, id) =>
  listChecklist(type).find((x) => x.id === id) || null;

/** Шинээр нэмэх (DB) */
export const addChecklistItem = async (type, payload) => {
  const list = listChecklist(type);
  const maxNo = list.reduce((m, x) => Math.max(m, x.no || 0), 0);
  await xabPreworkChecklistApi.create({
    checklist_type: type,
    no: Number(payload?.no) || maxNo + 1,
    title: String(payload?.title || "").trim(),
    description: String(payload?.description || "").trim(),
    requires_photo: !!payload?.requires_photo,
    is_critical: type === "CRITICAL" ? true : !!payload?.is_critical,
  });
  return loadChecklist(type);
};

/** Засах (DB) */
export const updateChecklistItem = async (type, id, patch) => {
  const body = {};
  if (patch?.no != null) body.no = Number(patch.no);
  if (patch?.title != null) body.title = String(patch.title).trim();
  if (patch?.description != null)
    body.description = String(patch.description).trim();
  if (patch?.requires_photo != null)
    body.requires_photo = !!patch.requires_photo;
  if (patch?.is_critical != null) body.is_critical = !!patch.is_critical;
  await xabPreworkChecklistApi.update(id, body);
  return loadChecklist(type);
};

/** Устгах (DB) */
export const deleteChecklistItem = async (type, id) => {
  await xabPreworkChecklistApi.remove(id);
  return loadChecklist(type);
};

/** Анхдагч seed рүү буцаах (backend re-seed) */
export const resetChecklist = async (type) => {
  await xabPreworkChecklistApi.reset(type);
  return loadChecklist(type);
};
